import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { IdentityService } from './services/identity.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from './core/loading-overlay/loading.service';
import { snackBarConfig } from './core/core.module';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { TranslationHandleService } from './core/translate/translation-handle.service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { WorkerTopic } from '../worker/app-workers/shared/worker-topic.enum';
import { ZendeskService } from './services/zendesk.service';
import { Locales } from './shared/enums/locales';
import { User } from './modules/users/models/user';
import { UsersService } from './modules/users/services/users.service';
import { TelemetryEventService } from './services/telemetry-event.service';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

const PHRASES_FOR_TRANSLATE = [
  {
    welcome: 'commonPhrases.welcome',
  },
  {
    sessionClosed: 'system.sessionClosed',
  },
  {
    logout: 'sidebar.logout',
  },
];

@Component({
  selector: 'mee-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  translationArray: Map<string, string>;
  isLoading = false;

  workerTopic = WorkerTopic.UpdateNode;

  constructor(
    public loadingService: LoadingService,
    private router: Router,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private identityService: IdentityService,
    private translationService: TranslationHandleService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly zendeskService: ZendeskService,
    private readonly usersService: UsersService,
    private readonly telemetryEventService: TelemetryEventService,
  ) {
    const languages = Object.values(Locales);
    translate.addLangs(languages);
    translate.setDefaultLang(Locales.English);

    this.initLanguage()
      .pipe(
        switchMap(() => this.initContactUsForm()),
        filter(isInit => !!isInit),
        tap(() => this.setContactUsFormData(this.identityService.user)),
      )
      .subscribe();

    this.translationService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
    this.translationService.getTranslationMap().subscribe(res => (this.translationArray = res));
    this.telemetryEventService.runActiveTabWatcher();
    this.telemetryEventService.runTelemetrySending();
  }

  ngOnInit() {
    this.loadingService
      .isLoading$()
      .pipe(
        debounceTime(100),
        tap(isLoading => {
          this.isLoading = isLoading;
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe();

    this.identityService.onSignIn
      .pipe(
        tap(() => this.identityService.resetMfaResendData()),
        switchMap(() => this.usersService.getMeData()),
        switchMap((userInformation: User) => {
          this.identityService.user = userInformation;
          this.zendeskService.initZendesk().then(() => {
            this.zendeskService.setContactFormData(userInformation);
            this.zendeskService.showWidget();
          });

          return this.translate.use(userInformation.Locale ?? Locales.English);
        }),
        tap(() => {
          this.translationService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
          this.router.navigate(['/dashboard']).then(() => {
            this.snackBar.open(this.translationArray.get('welcome'), null, snackBarConfig);
          });
        }),
        catchError(err => {
          this.snackBar.open(err.statusText, 'OK', snackBarConfig);
          return throwError(err);
        }),
      )
      .subscribe();

    this.identityService.onLogout
      .pipe(
        switchMap(() => this.translate.use(Locales.English)),
        tap(() => {
          this.translationService.setPhrasesForTranslate(PHRASES_FOR_TRANSLATE);
          this.snackBar.open(this.translationArray.get('sessionClosed'), null, {
            duration: 5000,
          });

          this.zendeskService.setContactFormData(null);
          this.zendeskService.hideWidget();
          this.router.navigate(['sign-in']);
        }),
      )
      .subscribe();
  }

  logout() {
    this.identityService.logout();
  }

  private initLanguage(): Observable<any> {
    const user = this.identityService.user;
    let locale = Locales.English;

    if (!this.identityService.identity) {
      return EMPTY;
    }

    if (user) {
      locale = user.Locale ?? Locales.English;

      return this.translate.use(locale);
    }

    return this.usersService.getMeData().pipe(
      switchMap(userInfo => {
        this.identityService.user = userInfo;
        locale = userInfo.Locale ?? Locales.English;

        return this.translate.use(locale);
      }),
    );
  }

  private initContactUsForm(): Observable<boolean> {
    return fromPromise(this.zendeskService.initZendesk());
  }

  private setContactUsFormData(userInformation: User): void {
    this.zendeskService.setContactFormData(userInformation);
    this.zendeskService.setWidgetLanguage(userInformation.Locale);
  }
}
